import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { axiosInstance } from '../services/axiosInstance';
import {
  Grade,
  Grants,
  defaultGrants,
  isGranted,
  normalizeGrants,
  shellKeepsLink,
  viewerGrade,
} from './menuCatalog';

interface MenuPolicyValue {
  grants: Grants;
  refresh: () => Promise<void>;
  replaceGrants: (grants: Grants) => void;
  grantedForViewer: (id: string) => boolean;
  allowNav: (id: string) => boolean;
  allowShellLink: (id: string | null) => boolean;
}

const MenuPolicyContext = createContext<MenuPolicyValue | undefined>(undefined);

export const MenuPolicyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuth();
  const [grants, setGrants] = useState<Grants>(() => defaultGrants());

  const refresh = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/brand-menu/policy');
      if (data?.grants && typeof data.grants === 'object') {
        setGrants(normalizeGrants(data.grants as Grants));
      }
    } catch {
      setGrants(defaultGrants());
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const replaceGrants = useCallback((next: Grants) => {
    setGrants(normalizeGrants(next));
  }, []);

  const grade = viewerGrade(authState.user, authState.isAuthenticated);

  const grantedForViewer = useCallback(
    (id: string) => {
      if (grade === 'brand') return true;
      return isGranted(id, grade, grants);
    },
    [grade, grants],
  );

  const allowNav = useCallback(
    (id: string) => {
      if (authState.user?.is_brand_manager) return true;
      const navGrade: Grade = authState.user?.is_superuser ? 'super' : 'manager';
      return isGranted(id, navGrade, grants);
    },
    [authState.user, grants],
  );

  const allowShellLink = useCallback(
    (id: string | null) => {
      if (!id) return true;
      if (grade === 'brand') return true;
      const navGrade: Grade = grade === 'super' ? 'super' : grade === 'manager' ? 'manager' : 'facility';
      return shellKeepsLink(id, navGrade, isGranted(id, navGrade, grants));
    },
    [grade, grants],
  );

  const value = useMemo(
    () => ({ grants, refresh, replaceGrants, grantedForViewer, allowNav, allowShellLink }),
    [grants, refresh, replaceGrants, grantedForViewer, allowNav, allowShellLink],
  );

  return <MenuPolicyContext.Provider value={value}>{children}</MenuPolicyContext.Provider>;
};

export function useMenuPolicy(): MenuPolicyValue {
  const ctx = useContext(MenuPolicyContext);
  if (!ctx) {
    throw new Error('MenuPolicyProvider가 필요합니다.');
  }
  return ctx;
}

export function useKeepGrantedTab(
  active: number,
  menuIds: readonly string[],
  setActive: (index: number) => void,
): (index: number) => boolean {
  const { grantedForViewer } = useMenuPolicy();
  const visible = useCallback(
    (index: number) => {
      const id = menuIds[index];
      if (!id) return true;
      return grantedForViewer(id);
    },
    [grantedForViewer, menuIds],
  );

  useEffect(() => {
    if (visible(active)) return;
    const next = menuIds.findIndex((_, index) => visible(index));
    if (next >= 0 && next !== active) setActive(next);
  }, [active, menuIds, setActive, visible]);

  return visible;
}
